import { injectLocal, provideLocal } from "@vueuse/core";
import { type InjectionKey, ref, watch } from "vue";
import { graphql as gql } from "@/generated";
import { useMutation } from "@vue/apollo-composable";
import { useChannelStore } from "@/stores/channel.store";
import {
  ConversionType,
  ExperienceType,
  type OpenGraphDefinitionInput,
  type User,
} from "@/generated/graphql";
import { useSingleToast, useToastStore } from "@/stores/toast.store";
import { apolloClient } from "@/utils/apollo";
import { useTrackingStore } from "@/stores/tracking.store";
import { TrackEventEnum } from "@/services/tracking/tracking-types";
import { getEventCategoryFromExperienceType } from "@/utils/referrals";
import {
  ChannelEventType,
  useChannelEvents,
} from "@/composables/channel-events";
import { useUserStore } from "@/stores/user.store";
import { useDumbStorage } from "@/composables/use-dumb-storage";
import { z } from "zod/v4";
import { useI18n } from "vue-i18n";
import { getShareableLinkBaseUrl } from "@/utils/app-navigator";

const referralServiceLogPrefix = "[ReferralService] ";

export const referralServiceInjectionKey = Symbol(
  "ReferralService",
) as InjectionKey<ReturnType<typeof useSetupReferralService>>;

export type ReferralPayload = {
  experience_id?: string;
  experience_type?: ExperienceType;
  community_id?: string | undefined;
};

export type ReferralTrackingData = {
  code: string;
  referrer: User;
  payload: ReferralPayload;
  hasBeenProcessed: boolean;
  ogImageUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
};

const referralTrackingDataSchema = z.object({
  code: z.string(),
  referrer: z.object({
    id: z.string(),
    username: z.string(),
    avatar: z.preprocess((val) => val ?? undefined, z.string().optional()),
    is_verified: z.boolean(),
    locale: z.string(),
  }),
  payload: z.object({
    experience_id: z.preprocess(
      (val) => val ?? undefined,
      z.string().optional(),
    ),
    experience_type: z.preprocess(
      (val) => val ?? undefined,
      z.enum(ExperienceType).optional(),
    ),
    community_id: z.preprocess(
      (val) => val ?? undefined,
      z.string().optional(),
    ),
  }),
  hasBeenProcessed: z.boolean(),
  ogImageUrl: z.string().optional(),
  ogTitle: z.string().optional(),
  ogDescription: z.string().optional(),
  ogType: z.string().optional(),
});

const CreateReferralLinkDocument = gql(/* GraphQL */ `
  mutation CreateWebLink(
    $payload: WebLinkPayloadInput!
    $openGraphPayload: OpenGraphDefinitionInput!
    $channel_id: String!
    $origin: String!
  ) {
    createWebLink(
      payload: $payload
      open_graph_payload: $openGraphPayload
      channel_id: $channel_id
      origin: $origin
    ) {
      ... on WebLinkCreated {
        channel_id
        referrer {
          id
          username
          avatar
          is_verified
          locale
        }
        link
        payload {
          experience_id
          experience_type
          community_id
        }
      }
      ... on UnauthenticatedError {
        error
      }
      ... on ThirdPartyBranchError {
        error
        reason
      }
      ... on InvalidOriginError {
        error
        origin
      }
    }
  }
`);

const WebLinkDocument = gql(/* GraphQL */ `
  query WebLink($code: String!) {
    webLink(code: $code) {
      ... on WebLink {
        channel_id
        referrer {
          id
          username
          avatar
          is_verified
          locale
        }
        og_title
        og_description
        og_type
        og_image_url
        payload {
          experience_type
          experience_id
          community_id
        }
      }
      ... on NoChannelError {
        error
      }
      ... on UnknownWebLinkCodeError {
        error
        code
      }
    }
  }
`);

const ProcessReferralConversionDocument = gql(/* GraphQL */ `
  mutation ProcessReferralConversion(
    $conversionType: ConversionType!
    $code: String!
  ) {
    processWebLinkReferralConversion(
      conversion_type: $conversionType
      code: $code
    ) {
      ... on WebLinkReferralResponse {
        success
      }
      ... on UnknownWebLinkCodeError {
        error
        code
      }
      ... on NoChannelError {
        error
      }
      ... on UnauthenticatedError {
        error
      }
    }
  }
`);

const ProcessReferralCheckInDocument = gql(/* GraphQL */ `
  mutation ProcessReferralCheckIn($code: String!) {
    processWebLinkReferralCheckIn(code: $code) {
      ... on WebLinkReferralResponse {
        success
      }
      ... on UnknownWebLinkCodeError {
        error
        code
      }
      ... on NoChannelError {
        error
      }
      ... on UnauthenticatedError {
        error
      }
    }
  }
`);

const experienceTypesWithCheckInProcesses = [ExperienceType.OnsiteEvent];

/**
 * Handles link-sharing to process referral data and track the related UserActions.
 *
 * The process should be as follows:
 * 1. A page process the referral code and calls `handleReferralCode` to set the referral data.
 * 2. A modal is shown to the user to inform them about the referral and ask them to take an action.
 *
 * When processing the referral, if the user doesn't have an account yet, when interacting
 * with the content, they should be prompted to sign up or log in, after which the app should call
 * `processReferralConversion` to track the conversion.
 *
 */
export function useSetupReferralService() {
  const currentReferralData = useDumbStorage<ReferralTrackingData | null>(
    "fanfest:currentReferralData",
    null,
    {
      serializer: {
        read: (value) => {
          if (!value) return null;
          try {
            const parsed = JSON.parse(value);
            return referralTrackingDataSchema.parse(parsed);
          } catch (e) {
            console.error(
              referralServiceLogPrefix +
                "Unable to parse referral data from local storage",
              e,
            );
            return null;
          }
        },
        write: (value) => {
          if (!value) return null;
          return JSON.stringify(value);
        },
      },
    },
  );
  const showReferralModal = ref(false);

  // When the referral is processed and the modal is closed, reset the referral data.
  watch(showReferralModal, (value) => {
    if (value === false && currentReferralData.value?.hasBeenProcessed) {
      currentReferralData.value = null;
    }
  });

  const { t } = useI18n();
  const channelStore = useChannelStore();
  const userStore = useUserStore();
  const toastStore = useToastStore();
  const trackingService = useTrackingStore();
  const { emitChannelEvent, watchEventsEffect } = useChannelEvents();
  watchEventsEffect((event) => {
    const shouldAcceptEvent =
      (event.type === ChannelEventType.AUTHENTICATION_FINISHED &&
        !event.willUpgrade) ||
      event.type === ChannelEventType.UPGRADE_FINISHED;

    if (!shouldAcceptEvent) return;

    const referralType = currentReferralData.value?.payload.experience_type;
    const hasReferralWithCheckIn =
      referralType &&
      experienceTypesWithCheckInProcesses.includes(referralType);

    if (hasReferralWithCheckIn) {
      processReferralCheckIn();
    }
  });

  const { mutate: createReferralLinkMutation } = useMutation(
    CreateReferralLinkDocument,
  );

  const { mutate: processWebLinkCheckIn } = useMutation(
    ProcessReferralCheckInDocument,
  );

  const { mutate: processWebLinkReferralConversion } = useMutation(
    ProcessReferralConversionDocument,
  );

  const fetchingReferralLoadingToast = useSingleToast();
  async function createReferralLink(
    openGraph: OpenGraphDefinitionInput,
    payload: ReferralPayload,
  ): Promise<string | null> {
    fetchingReferralLoadingToast.set({
      message: t("ui.alerts.creatingReferralLink"),
      type: "info",
    });

    const result = await createReferralLinkMutation({
      channel_id: channelStore.channelSlug!,
      origin: await getShareableLinkBaseUrl(),
      openGraphPayload: openGraph,
      payload:
        "experience_id" in payload
          ? payload
          : {
              experience_id: null,
              experience_type: null,
              community_id: payload.community_id,
            },
    });

    const data = result?.data?.createWebLink;

    if (result?.errors || !data) {
      console.error(
        referralServiceLogPrefix +
          "Unknown error occurred while creating referral link",
        result?.errors,
      );
      fetchingReferralLoadingToast.finished({
        message: t("ui.alerts.unableToCreateReferralLinkUnknownError"),
        type: "error",
      });
      return null;
    }

    if ("error" in data) {
      console.error(
        referralServiceLogPrefix + "Unable to create referral link",
        data,
      );
      switch (data.__typename) {
        case "UnauthenticatedError": {
          fetchingReferralLoadingToast.finished({
            message: t("ui.alerts.youMustBeLoggedInToCreateReferralLink"),
            type: "error",
          });
          return null;
        }
        case "InvalidOriginError": {
          fetchingReferralLoadingToast.finished({
            message: t("ui.alerts.unableToCreateReferralLinkInvalidOrigin", {
              origin: data.origin,
            }),
            type: "error",
          });
          return null;
        }
        case "ThirdPartyBranchError": {
          fetchingReferralLoadingToast.finished({
            message: t("ui.alerts.unableToCreateReferralLinkThirdPartyError", {
              reason: data.reason,
            }),
            type: "error",
          });
          return null;
        }
      }
      return null;
    }

    fetchingReferralLoadingToast.finished({
      message: t("ui.alerts.referralLinkCreated"),
      type: "success",
    });

    return data.link;
  }

  async function handleReferralCode(code: string) {
    const result = await apolloClient.query({
      query: WebLinkDocument,
      variables: {
        code: code,
      },
    });

    const data = result?.data?.webLink;

    if (result?.errors || !data) {
      console.error(
        referralServiceLogPrefix +
          "Unknown error occurred while processing referral link",
        result?.errors,
      );
      toastStore.addToast({
        message: t("ui.alerts.unableToProcessReferralLinkUnknownError"),
        type: "error",
      });
      return null;
    }

    if ("error" in data) {
      console.error(
        referralServiceLogPrefix + "Unable to process referral data",
        data,
      );
      switch (data.__typename) {
        case "UnknownWebLinkCodeError": {
          toastStore.addToast({
            message: t("ui.alerts.referralLinkBrokenOrMissing"),
            type: "error",
          });
          return null;
        }
      }
      return null;
    }

    currentReferralData.value = {
      code,
      referrer: data.referrer,
      payload: data.payload as ReferralPayload,
      hasBeenProcessed: false,

      ogImageUrl: data.og_image_url ?? undefined,
      ogTitle: data.og_title ?? undefined,
      ogDescription: data.og_description ?? undefined,
      ogType: data.og_type ?? undefined,
    };

    // Switch theme to the selected theme on generate referral link
    const communityId = data.payload.community_id;

    // Only set the temporary selected community if there are multiple communities for the channel
    if (communityId && channelStore.channelCommunities.length > 1) {
      await channelStore.setTemporarySelectedCommunity(communityId);
    }

    trackingService.track({
      event: TrackEventEnum.VisitFromReferralLink,
      category: getEventCategoryFromExperienceType(
        currentReferralData.value?.payload?.experience_type,
      ),
      metadata: {
        experienceId: currentReferralData.value?.payload?.experience_id,
        experienceType: currentReferralData.value?.payload?.experience_type,
        experienceTitle: currentReferralData.value?.ogTitle ?? undefined,
        referrer: currentReferralData?.value?.referrer,
      },
    });

    const isChannelReferral =
      !currentReferralData.value?.payload?.experience_type;

    const isAuthenticated = userStore.isAuthenticated;
    if (isChannelReferral && !isAuthenticated) {
      emitChannelEvent({
        type: ChannelEventType.SHOW_AUTH_MODAL,
        referrer: data.referrer,
      });
    } else {
      showReferralModal.value = true;
    }
  }

  /**
   * Gets called after the user interacts with the referral content and "checks in".
   *
   * Note: Not all experience types can be processed via a "check in" / simple action.
   */
  async function processReferralCheckIn(): Promise<boolean> {
    const referralData = currentReferralData.value;

    if (
      !referralData ||
      !referralData.payload.experience_type ||
      // Only process check-ins for types that allow so
      !experienceTypesWithCheckInProcesses.includes(
        referralData.payload.experience_type,
      )
    ) {
      return false;
    }

    const result = await processWebLinkCheckIn({
      code: referralData.code,
    });

    const data = result?.data?.processWebLinkReferralCheckIn;

    if (result?.errors || !data) {
      console.error(
        referralServiceLogPrefix +
          "Unknown error occurred while processing referral link",
        result?.errors,
      );
      toastStore.addToast({
        message: t("ui.alerts.unableToProcessReferralLinkUnknownError"),
        type: "error",
      });
      return false;
    }

    if ("error" in data) {
      console.error(
        referralServiceLogPrefix + "Unable to record referral conversion",
        data,
      );
      switch (data.__typename) {
        case "UnknownWebLinkCodeError": {
          toastStore.addToast({
            message: t("ui.alerts.referralLinkBrokenOrMissing"),
            type: "error",
          });
          return false;
        }
      }
      return false;
    }

    trackingService.track({
      event: TrackEventEnum.ReferralLinkConversion,
      category: getEventCategoryFromExperienceType(
        currentReferralData.value?.payload?.experience_type,
      ),
      metadata: {
        experienceId: currentReferralData.value?.payload?.experience_id,
        experienceType: currentReferralData.value?.payload?.experience_type,
        experienceTitle: currentReferralData.value?.ogTitle ?? undefined,
        referrer: currentReferralData?.value?.referrer,
      },
    });

    currentReferralData.value = null;
    showReferralModal.value = false;

    return data.success;
  }

  /**
   * Gets called after the user interacts with the referral content and completes the conversion,
   * by signing up and then purchasing a subscription (either paid or free).
   *
   * After processing the conversion, the referral modal is shown again.
   */
  async function processReferralConversion(
    conversionType: ConversionType,
  ): Promise<boolean> {
    const referralData = currentReferralData.value;

    if (
      !referralData ||
      // Only process conversions for types that don't allow check-ins
      (referralData.payload.experience_type !== undefined &&
        experienceTypesWithCheckInProcesses.includes(
          referralData.payload.experience_type,
        ))
    ) {
      return false;
    }

    const result = await processWebLinkReferralConversion({
      code: referralData.code,
      conversionType,
    });

    const data = result?.data?.processWebLinkReferralConversion;

    if (result?.errors || !data) {
      console.error(
        referralServiceLogPrefix +
          "Unknown error occurred while processing referral link",
        result?.errors,
      );
      toastStore.addToast({
        message: t("ui.alerts.unableToProcessReferralLinkUnknownError"),
        type: "error",
      });
      return false;
    }

    if ("error" in data) {
      console.error(
        referralServiceLogPrefix + "Unable to record referral conversion",
        data,
      );
      switch (data.__typename) {
        case "UnknownWebLinkCodeError": {
          toastStore.addToast({
            message: t("ui.alerts.referralLinkBrokenOrMissing"),
            type: "error",
          });
          return false;
        }
      }
      return false;
    }

    trackingService.track({
      event: TrackEventEnum.ReferralLinkConversion,
      category: getEventCategoryFromExperienceType(
        currentReferralData.value?.payload?.experience_type,
      ),
      metadata: {
        experienceId: currentReferralData.value?.payload?.experience_id,
        experienceType: currentReferralData.value?.payload?.experience_type,
        experienceTitle: currentReferralData.value?.ogTitle ?? undefined,
        referrer: currentReferralData?.value?.referrer,
      },
    });

    return data.success;
  }

  async function onInvalidReferralPayload() {
    const toastStore = useToastStore();

    toastStore.addToast({
      message: t("ui.alerts.referralLinkBrokenOrMissing"),
      type: "error",
    });

    currentReferralData.value = null;
  }

  function resetAndCloseReferralModal() {
    currentReferralData.value = null;
    showReferralModal.value = false;
  }

  const definition = {
    showReferralModal,
    currentReferralData,
    createReferralLink,
    handleReferralCode,
    processReferralCheckIn,
    processReferralConversion,
    onInvalidReferralPayload,
    resetAndCloseReferralModal,
  };
  provideLocal(referralServiceInjectionKey, definition);
  return definition;
}

export function useReferralService() {
  const value = injectLocal(referralServiceInjectionKey);

  if (!value) {
    throw new Error(
      referralServiceLogPrefix + "Referral service is not provided.",
    );
  }

  return value;
}
